import { useEffect, useState } from 'react';
import { HiOutlineDownload, HiOutlineX, HiOutlineCheck, HiOutlineDeviceMobile, HiOutlineShare, HiOutlineBell } from 'react-icons/hi';
import Modal from './Modal';
import API from '../../utils/api';

/**
 * PWAInstallPrompt
 * - Listens for the browser's native beforeinstallprompt event (Chrome/Edge)
 * - Shows a beautiful branded modal encouraging the user to install
 * - For browsers that don't support it (HTTP, iOS Safari), shows manual
 *   instructions ("Share → Add to Home Screen")
 * - Hides permanently after install or explicit dismiss
 * - Skipped for desktop / standalone (already installed) sessions
 */
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isSecure, setIsSecure] = useState(true);

  useEffect(() => {
    // Detect iOS Safari
    const ua = window.navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(ios);

    // Check secure context (HTTPS or localhost)
    setIsSecure(window.isSecureContext);

    // If app is already running as installed PWA, never show
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    // Don't show if user dismissed recently (localStorage flag, 3-day cooldown)
    const lastDismiss = parseInt(localStorage.getItem('pwaInstallDismissed') || '0', 10);
    if (Date.now() - lastDismiss < 3 * 24 * 60 * 60 * 1000) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 1200);
    };

    const handleInstalled = () => {
      setInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    // For iOS or insecure context (HTTP IP), show manual prompt after delay
    if (ios || !window.isSecureContext) {
      const t = setTimeout(() => setShowPrompt(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        window.removeEventListener('appinstalled', handleInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setShowPrompt(false);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        setInstalled(true);
      }
    } catch (err) {
      // User dismissed natively — that's fine
    } finally {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwaInstallDismissed', String(Date.now()));
  };

  if (installed) return null;

  // iOS Safari — manual instructions
  if (isIOS) {
    return (
      <Modal isOpen={showPrompt} onClose={handleDismiss} title="" size="md">
        <div className="text-center space-y-4 pb-2">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-brand rounded-full blur-2xl opacity-50 animate-pulse" />
              <div className="relative w-20 h-20 rounded-3xl bg-gradient-brand flex items-center justify-center shadow-2xl">
                <HiOutlineDeviceMobile className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-ink-900 dark:text-white mb-1">📱 অ্যাপ ইনস্টল করুন</h2>
            <p className="text-sm text-ink-600 dark:text-ink-400">iPhone এ সহজে ইনস্টল করুন</p>
          </div>

          <div className="space-y-2.5 text-left">
            <div className="flex items-start gap-3 p-2.5 rounded-xl bg-brand-50 dark:bg-brand-900/20">
              <div className="w-7 h-7 rounded-full bg-brand-500 text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">১</div>
              <div>
                <p className="text-sm font-semibold text-ink-900 dark:text-white">Share বাটনে ট্যাপ করুন</p>
                <p className="text-xs text-ink-600 dark:text-ink-400">নিচে <HiOutlineShare className="inline w-3 h-3" /> আইকন</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-2.5 rounded-xl bg-accent-50 dark:bg-accent-900/20">
              <div className="w-7 h-7 rounded-full bg-accent-500 text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">২</div>
              <div>
                <p className="text-sm font-semibold text-ink-900 dark:text-white">"হোম স্ক্রিনে যোগ করুন" সিলেক্ট</p>
                <p className="text-xs text-ink-600 dark:text-ink-400">Add to Home Screen</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20">
              <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">৩</div>
              <div>
                <p className="text-sm font-semibold text-ink-900 dark:text-white">"যোগ করুন" টিপুন</p>
                <p className="text-xs text-ink-600 dark:text-ink-400">অ্যাপ install হয়ে যাবে!</p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="btn-primary w-full inline-flex items-center justify-center gap-1"
          >
            <HiOutlineCheck className="w-4 h-4" /> বুঝেছি
          </button>
        </div>
      </Modal>
    );
  }

  // Android / Desktop with native prompt
  return (
    <Modal isOpen={showPrompt} onClose={handleDismiss} title="" size="md">
      <div className="text-center space-y-4 pb-2">
        <div className="flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-brand rounded-full blur-2xl opacity-50 animate-pulse" />
            <div className="relative w-20 h-20 rounded-3xl bg-gradient-brand flex items-center justify-center shadow-2xl">
              <HiOutlineDeviceMobile className="w-10 h-10 text-white" />
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-bold text-ink-900 dark:text-white mb-1">📱 অ্যাপ ইনস্টল করুন</h2>
          <p className="text-sm text-ink-600 dark:text-ink-400">একবার ইনস্টল করুন — সবসময় আঙুলের ডগায়!</p>
        </div>

        {!isSecure && (
          <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-left">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">⚠️ HTTPS দরকার</p>
            <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
              Chrome শুধুমাত্র HTTPS এ install option দেখায়। SSL certificate যোগ করলে install বাটন দেখবে।
            </p>
          </div>
        )}

        <div className="space-y-2.5 text-left">
          <div className="flex items-start gap-3 p-2.5 rounded-xl bg-gradient-to-r from-brand-50 to-transparent dark:from-brand-900/20">
            <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center flex-shrink-0">
              <HiOutlineCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-ink-900 dark:text-white">দ্রুত অ্যাক্সেস</p>
              <p className="text-xs text-ink-600 dark:text-ink-400">হোম স্ক্রিন থেকে এক ট্যাপে খুলুন</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-2.5 rounded-xl bg-gradient-to-r from-accent-50 to-transparent dark:from-accent-900/20">
            <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
              <HiOutlineCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-ink-900 dark:text-white">ফুলস্ক্রিন মোড</p>
              <p className="text-xs text-ink-600 dark:text-ink-400">ব্রাউজার বার ছাড়া, native অ্যাপের মতো</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-2.5 rounded-xl bg-gradient-to-r from-emerald-50 to-transparent dark:from-emerald-900/20">
            <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <HiOutlineCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-ink-900 dark:text-white">অফলাইনে ব্যবহার</p>
              <p className="text-xs text-ink-600 dark:text-ink-400">ইন্টারনেট ছাড়াও পেজ লোড হবে</p>
            </div>
          </div>
        </div>

        {/* Manual install hint for Android/desktop (Chrome) */}
        <div className="p-2.5 rounded-xl bg-ink-50 dark:bg-ink-900/30 text-left">
          <p className="text-[11px] font-bold text-ink-700 dark:text-ink-200 mb-1">📌 ইনস্টল করতে:</p>
          <p className="text-[11px] text-ink-600 dark:text-ink-300">
            Chrome মেনু (⋮) → <strong>"Install app"</strong> অথবা address bar এর পাশে <strong>"⊕ Install"</strong> আইকনে ক্লিক করুন।
          </p>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={handleDismiss}
            className="btn-secondary flex-1 inline-flex items-center justify-center gap-1"
          >
            <HiOutlineX className="w-4 h-4" />
            পরে
          </button>
          {deferredPrompt && (
            <button
              type="button"
              onClick={handleInstall}
              className="btn-primary flex-1 inline-flex items-center justify-center gap-1"
            >
              <HiOutlineDownload className="w-4 h-4" />
              ইনস্টল করুন
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PWAInstallPrompt;
